import type { ReactElement } from 'react';
import {
  AGENT_UPDATE_HEALTH_WINDOW_MINUTES,
  AGENT_UPDATE_PENDING_REBOOT_HOURS,
  type AgentUpdateState,
} from '@kirin/api-client/contracts/agent-auto-update';
import { AGENT_UPDATE_STATE_LABELS } from './agentUpdateStateLabels';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const DESCRIPTIONS: Record<AgentUpdateState, string> = {
  up_to_date: 'The agent runs the latest Kirin release.',
  updating: 'The agent is installing the latest Kirin release.',
  pending_reboot:
    'The new version is installed and Windows completes it at the next restart. The agent keeps running its current version until then.',
  needs_attention: `The Kirin helper on the machine did not respond, the new version did not report back within ${AGENT_UPDATE_HEALTH_WINDOW_MINUTES} minutes, or Windows has not restarted within ${AGENT_UPDATE_PENDING_REBOOT_HOURS / 24} days to finish the install. Check the machine, or reinstall Kirin with its installer.`,
  update_failed:
    'The last update attempt failed. The agent keeps its current version and tries again at its next check.',
  pinned_by_mdm:
    'Your MDM installed a fixed version, so the agent does not update past it.',
  not_available:
    'Kirin was installed without the Kirin helper (for example with the one-line install script), or on Linux from a package that is not signed, so it cannot update by itself. Reinstall it with the Kirin installer package, or upgrade it to a signed Linux package, to get automatic updates.',
  off: 'Automatic agent updates are off for your organization.',
};

const STYLES: Record<AgentUpdateState, string> = {
  up_to_date:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  updating: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  pending_reboot:
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  needs_attention:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  update_failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  pinned_by_mdm:
    'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  not_available: 'bg-muted text-muted-foreground',
  off: 'bg-muted text-muted-foreground',
};

interface AgentUpdateStateBadgeProps {
  state: AgentUpdateState;
  className?: string;
}

/**
 * KIR-5191: a pill for an agent's automatic-update state, with a tooltip that
 * says what the state means.
 */
export function AgentUpdateStateBadge({
  state,
  className,
}: AgentUpdateStateBadgeProps): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid="agent-update-state"
          data-state={state}
          className={cn(
            'inline-flex cursor-help items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium',
            STYLES[state],
            className
          )}
        >
          {AGENT_UPDATE_STATE_LABELS[state]}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        {DESCRIPTIONS[state]}
      </TooltipContent>
    </Tooltip>
  );
}

AgentUpdateStateBadge.displayName = 'AgentUpdateStateBadge';
